import React, { FormEvent, useState } from 'react';
import heroImage from '../assets/triviyolo-teaser-hero.png';
import './Home.css';

type SignupState = 'idle' | 'submitted';

const features = [
  {
    icon: 'fa-bolt',
    title: 'Live game rooms',
    body: 'Host real-time trivia nights with fast rounds, player presence, score swings, and a lobby that feels alive before the first question drops.',
  },
  {
    icon: 'fa-layer-group',
    title: 'Curated collections',
    body: 'Build reusable question sets for teams, classrooms, parties, communities, and creators who want sharp content without spreadsheet chaos.',
  },
  {
    icon: 'fa-signal',
    title: 'Made for momentum',
    body: 'SignalR-powered gameplay keeps the room synchronized so hosts can move from question to answer to leaderboard without losing the crowd.',
  },
];

const milestones = [
  'Multiplayer game flow',
  'Host GameSpaces',
  'Social sign-in',
  'Creator-ready content tools',
];

export default function Home() {
  const [email, setEmail] = useState('');
  const [signupState, setSignupState] = useState<SignupState>('idle');

  const handleSignup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      return;
    }

    localStorage.setItem('triviyolo-teaser-email', email.trim());
    setSignupState('submitted');
  };

  return (
    <div className="teaser-page">
      <section className="teaser-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="teaser-hero__shade" />
        <div className="teaser-hero__content">
          <p className="teaser-kicker">Real-time trivia is loading</p>
          <h1>TriviYolo</h1>
          <p className="teaser-hero__copy">
            A social trivia platform for hosts, players, classrooms, teams, and creators who want live games that move fast and feel effortless.
          </p>

          <form className="teaser-signup" onSubmit={handleSignup}>
            <label htmlFor="teaser-email" className="visually-hidden">
              Email address
            </label>
            <input
              id="teaser-email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setSignupState('idle');
              }}
              placeholder="Email for launch updates"
              aria-describedby="teaser-signup-status"
            />
            <button type="submit">
              <i className="fas fa-paper-plane" aria-hidden="true" />
              <span>Get Updates</span>
            </button>
          </form>

          <p id="teaser-signup-status" className="teaser-signup__status" aria-live="polite">
            {signupState === 'submitted'
              ? "You're on the local preview list. We'll wire this to the real launch list next."
              : 'Private previews are being prepared while the platform is in development.'}
          </p>

          <div className="teaser-access-note" aria-label="Preview access status">
            <i className="fas fa-lock" aria-hidden="true" />
            <span>Player access opens when private previews begin.</span>
          </div>
        </div>
      </section>

      <section className="teaser-strip" aria-label="Platform highlights">
        <div>
          <strong>Instant multiplayer</strong>
          <span>Lobby, live rounds, and answer reveal</span>
        </div>
        <div>
          <strong>Built for hosts</strong>
          <span>GameSpaces, collections, and session control</span>
        </div>
        <div>
          <strong>Creator friendly</strong>
          <span>CMS tooling for richer question libraries</span>
        </div>
      </section>

      <section className="teaser-section teaser-section--light">
        <div className="teaser-section__intro">
          <p className="teaser-kicker">What is coming</p>
          <h2>Trivia nights with less setup and more spark.</h2>
        </div>

        <div className="teaser-feature-grid">
          {features.map((feature) => (
            <article className="teaser-feature" key={feature.title}>
              <i className={`fas ${feature.icon}`} aria-hidden="true" />
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="teaser-section teaser-section--status">
        <div className="teaser-status-copy">
          <p className="teaser-kicker">Development preview</p>
          <h2>Core systems are coming together.</h2>
          <p>
            TriviYolo is being built on a .NET backend, React frontend, and real-time multiplayer sync. The teaser stays public while the product moves toward broader playtesting.
          </p>
        </div>

        <div className="teaser-roadmap" aria-label="Development milestones">
          {milestones.map((milestone, index) => (
            <div className="teaser-roadmap__item" key={milestone}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{milestone}</strong>
              <i className="fas fa-check" aria-hidden="true" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
